import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { interval, map, Observable, shareReplay, Subscription } from 'rxjs';
import { TimerTime } from './timer-time-model';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { WorkTimesService } from '@cl/core/services/work-times.service';
import { StartStopWorkTimeRequest } from '@cl/shared/models/work-times/start-stop-work-time-request.model';
import { WorkTime } from '@cl/shared/models/work-times/work-time.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { UpsertWorkTimeComponent } from '../upsert-work-time/upsert-work-time.component';

@Component({
    selector: 'cl-work-timer',
    templateUrl: './work-timer.component.html',
    styleUrl: './work-timer.component.less',
    standalone: false,
})
export class WorkTimerComponent implements OnInit, OnDestroy {
    GroupPermission = GroupPermissionsType;

    @Input() bigTimer: boolean = false;

    timeSpent$: Observable<TimerTime>;
    activeWorkTime: WorkTime;

    activeWorkTimeChangedSubscription: Subscription;

    constructor(
        private modalService: NzModalService,
        private workTimesService: WorkTimesService,
        private messageService: NzMessageService,
    ) {
        this.activeWorkTime = this.workTimesService.activeWorkTime;
        this.timeSpent$ = interval(1000).pipe(
            map((x): TimerTime => this.calculateTimeElapsed(new Date(this.workTimesService.activeWorkTime.startDateTimeUtc))),
            shareReplay(1),
        );
    }

    ngOnInit(): void {
        this.activeWorkTimeChangedSubscription = this.workTimesService.activeWorkTimeChanged.subscribe((newWorkTime: WorkTime) => {
            this.activeWorkTime = newWorkTime;
        });
    }

    async stopTimer(): Promise<void> {
        const modal: NzModalRef<UpsertWorkTimeComponent> = await this.workTimesService.createNotesModal(this.activeWorkTime);
        if (!modal) {
            return;
        }

        modal.afterClose.subscribe(async (workTime: WorkTime): Promise<void> => {
            if (!workTime.notes) {
                return;
            }

            try {
                const stopWorkTimerRequest = {
                    notes: workTime.notes,
                    workTimeId: this.activeWorkTime.id,
                } as StartStopWorkTimeRequest;

                await this.workTimesService.stopWorkTimer(stopWorkTimerRequest);
                this.activeWorkTime = null;
                this.workTimesService.setActiveWorkTime(null);
            } catch (ex) {
                console.error(ex);
                if (ex.error?.errors) {
                    ex.error.errors.forEach((error) => {
                        this.messageService.error(error);
                    });
                    return;
                }

                this.messageService.error('Neizdevās pabeigt laika uzskaiti');
                return;
            }
        });
    }

    async editWorkTime(): Promise<void> {
        const editableObj = await (await this.workTimesService.getSingle(this.activeWorkTime.id)).data;
        const modal = this.modalService.create({
            nzTitle: 'Laika uzskaites ieraksta labošana',
            nzContent: UpsertWorkTimeComponent,
        });

        modal.afterOpen.subscribe(() => {
            modal.getContentComponent().setEditMode(editableObj);
        });
    }

    formatTimePart(timePart: number): string {
        return timePart < 10 ? `0${timePart}` : `${timePart}`;
    }

    ngOnDestroy(): void {
        if (this.activeWorkTimeChangedSubscription) this.activeWorkTimeChangedSubscription.unsubscribe();
    }

    private calculateTimeElapsed(startDate: Date): TimerTime {
        const unixStartDate = startDate.valueOf();

        const millisecondsInSecond: number = 1000;
        const minutesInHour: number = 60;
        const secondsInMinute: number = 60;

        const difference: number = Date.now() - unixStartDate;

        const hoursFromStart: number = Math.floor(difference / (millisecondsInSecond * minutesInHour * secondsInMinute));
        const minutesFromStart: number = Math.floor((difference / (millisecondsInSecond * minutesInHour)) % secondsInMinute);
        const secondsFromStart: number = Math.floor(difference / millisecondsInSecond) % secondsInMinute;

        return { hoursElapsed: hoursFromStart, minutesElapsed: minutesFromStart, secondsElapsed: secondsFromStart } as TimerTime;
    }
}
